import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { studyAPI, projectAPI } from '../../services/api';
import { formatProjectLabel } from '../../utils/formatProjectLabel';
import { cleanFormData } from '../../utils/helpers';
import { toast } from 'react-toastify';
import useAutoGeneratedId from '../../hooks/useAutoGeneratedId';
import AutoGeneratedIdField from '../common/AutoGeneratedIdField';

const StudyForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    comments: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEditing);
  const [error, setError] = useState('');
  // Project membership: the projects assigned to this study (one study per
  // project, so adding a project already in another study moves it here).
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const navigate = useNavigate();

  const {
    id: previewId,
    nextId,
    loading: idLoading,
    error: idError,
    refetch: refetchId,
  } = useAutoGeneratedId('study', isEditing);

  useEffect(() => {
    if (isEditing) {
      const fetchStudy = async () => {
        try {
          const [studyRes, projectsRes] = await Promise.all([
            studyAPI.getById(id),
            studyAPI.getProjects(id),
          ]);
          setFormData({
            name: studyRes.data.name || '',
            description: studyRes.data.description || '',
            comments: studyRes.data.comments || '',
          });
          setSelectedProjects(projectsRes.data || []);
        } catch (err) {
          console.error('Error fetching study', err);
          setError('Failed to load study details');
        } finally {
          setFetchingData(false);
        }
      };
      fetchStudy();
    } else {
      setFetchingData(false);
    }
  }, [id, isEditing]);

  // Server-side project search for the membership picker.
  const searchProjects = async (term) => {
    if (!term || term.length < 2) {
      setProjectOptions([]);
      return;
    }
    setProjectSearchLoading(true);
    try {
      // High limit so searching a collaborator surfaces their whole project set.
      const response = await projectAPI.getAll(`?search=${encodeURIComponent(term)}&limit=100`);
      setProjectOptions(Array.isArray(response.data.projects) ? response.data.projects : []);
    } catch (err) {
      console.error('Error searching projects', err);
      setProjectOptions([]);
    } finally {
      setProjectSearchLoading(false);
    }
  };

  // Move hint — shown when a candidate project belongs to a different study, so
  // reassigning it here is visible, not silent.
  const otherStudyHint = (project) => {
    if (!project.study_id) return '';
    if (isEditing && project.study_id === id) return '';
    return project.study_name
      ? `Currently in: ${project.study_name} — will be moved here`
      : 'Currently in another study — will be moved here';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const cleanedData = cleanFormData(formData);
      // Always send the membership list so the backend reconciles add/remove/move.
      cleanedData.project_ids = selectedProjects.map((p) => p.id);

      if (isEditing) {
        await studyAPI.update(id, cleanedData);
        toast.success('Study updated successfully');
      } else {
        const response = await studyAPI.create(cleanedData);
        const created = response.data || response;
        toast.success(`Study "${created.name}" created`);
      }

      navigate('/studies');
    } catch (err) {
      console.error('Error saving study', err);
      setError('Failed to save study: ' + (err.response?.data?.msg || err.message));
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="study-form page-container">
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/studies" sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEditing ? 'Edit Study' : 'New Study'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Study Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                helperText="How this study is identified everywhere (e.g. the cohort name)"
              />
            </Grid>

            {!isEditing && (
              <Grid item xs={12} md={6}>
                <AutoGeneratedIdField
                  label="Study Number"
                  value={previewId}
                  nextId={nextId}
                  loading={idLoading}
                  error={idError}
                  onRefresh={refetchId}
                  isEditMode={isEditing}
                  helperText="Internal reference number, assigned automatically on creation"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="What this study covers"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                fullWidth
                disableCloseOnSelect
                options={projectOptions}
                value={selectedProjects}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                getOptionLabel={(option) => formatProjectLabel(option)}
                onChange={(event, newValue) => setSelectedProjects(newValue)}
                onInputChange={(event, newInput, reason) => {
                  // Only search on real typing. Selecting an option resets the
                  // input (reason 'reset') — ignore it so the result list (e.g.
                  // a collaborator's projects) stays put for ticking several.
                  if (reason === 'input') searchProjects(newInput);
                }}
                loading={projectSearchLoading}
                filterOptions={(x) => x}
                noOptionsText="Search by project, disease, PI, or collaborator…"
                renderOption={(props, option, { selected }) => {
                  const hint = otherStudyHint(option);
                  return (
                    <li {...props} key={option.id}>
                      <Checkbox sx={{ mr: 1 }} checked={selected} />
                      <Box>
                        <Typography variant="body2">
                          {formatProjectLabel(option)}
                          {option.pi_name ? ` · ${option.pi_name}` : ''}
                        </Typography>
                        {hint && (
                          <Typography variant="caption" color="warning.main">
                            {hint}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Projects in this study"
                    placeholder="Search by project, disease, PI, or collaborator…"
                    helperText="Search a collaborator (PI name or number) to list their projects, then tick several at once. A project already in another study will be moved here."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {projectSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments"
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="Any additional notes about this study"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button component={Link} to="/studies" variant="outlined" sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditing ? 'Update Study' : 'Create Study'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default StudyForm;
